import { AnchorHTMLAttributes, ButtonHTMLAttributes, forwardRef, type Ref } from "react";

import clsx from "clsx";
import Link from "next/link";

export enum ButtonVariants {
  link = "link",
  solid = "solid",
  outline = "outline",
}

export enum ButtonColorSchemes {
  primary = "primary",
  navy = "navy",
  white = "white",
  dark = "dark",
  secondary = "secondary",
}

export const buttonColorMapping = {
  [ButtonColorSchemes.dark]: "btn-dark",
  [ButtonColorSchemes.navy]: "btn-navy",
  [ButtonColorSchemes.white]: "btn-white",
  [ButtonColorSchemes.primary]: "btn-primary",
  [ButtonColorSchemes.secondary]: "btn-secondary",
};

type SharedProps = {
  variant?: ButtonVariants | keyof typeof ButtonVariants;
  color?: ButtonColorSchemes | keyof typeof ButtonColorSchemes;
};

type AnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & SharedProps & { href: string };
type NativeButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & SharedProps;

type ButtonProps = AnchorProps | NativeButtonProps;

const isLink = (props: ButtonProps): props is AnchorProps => "href" in props;

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  ({ className, variant, color = ButtonColorSchemes.primary, ...rest }, ref) => {
    const classes = clsx(
      className,
      buttonColorMapping[color],
      "btn",
      variant === ButtonVariants.outline && "btn-outline",
      variant === ButtonVariants.link && "btn-link"
    );

    if (isLink(rest)) {
      const linkRest = rest as AnchorProps;
      const linkContent = <a {...linkRest} className={classes} ref={ref as Ref<HTMLAnchorElement>} />;

      if (linkRest.href.startsWith("/")) {
        return <Link href={linkRest.href}>{linkContent}</Link>;
      }

      return linkContent;
    }

    const buttonRest = rest as NativeButtonProps;
    return <button type="button" {...buttonRest} className={classes} ref={ref as Ref<HTMLButtonElement>} />;
  }
);

Button.displayName = "Button";

export default Button;
